import { z } from 'zod';

import { ZodCompanyRestrictionsFlag } from '@Common/types/bo/b2b/companies/restriction_flags';
import { ZodProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const EmployeeRestrictionsFlagValues = [
  'block_voluntary_payments_and_withdrawals',
  'block_voluntary_payments',
  'block_withdrawals',
  'block_arbitrages',
  'block_selected_instruments_orders',
  'block_view_plan',
  'block_incoming_transfers',
  'block_matching_contribution',
  'block_triggered_arbitrages',
  'block_campaigns',
] as const;
export const ZodEmployeeRestrictionsFlag = z.enum(EmployeeRestrictionsFlagValues);

export type EmployeeRestrictionsFlag = z.TypeOf<typeof ZodEmployeeRestrictionsFlag>;

export const EmployeeRestrictionsProductValues = ['PEI', 'PERECOI'] as const;
const ZodEmployeeRestrictionsProduct = z.enum(EmployeeRestrictionsProductValues);

export type EmployeeRestrictionsProduct = z.TypeOf<typeof ZodEmployeeRestrictionsProduct>;

const ZodEmployeeRestrictionBase = z
  .object({
    restriction_flag: ZodEmployeeRestrictionsFlag,
    instruments: z.array(z.number().int()).optional(),
    product_type: ZodEmployeeRestrictionsProduct.nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.restriction_flag === 'block_campaigns') {
      if (data.product_type !== null) {
        ctx.addIssue({
          code: 'custom',
          message: 'product_type must be null for block_campaigns',
          path: ['product_type'],
        });
      }
    } else if (data.product_type === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'product_type is required for this restriction type',
        path: ['product_type'],
      });
    }
  });

export type EmployeeRestriction = z.TypeOf<typeof ZodEmployeeRestrictionBase>;

const ZodEmployeeRestrictionCreate = z.object({
  restrictions: z.array(ZodEmployeeRestrictionBase),
  employee_id: ZodId,
});

export type EmployeeRestrictionCreate = z.TypeOf<typeof ZodEmployeeRestrictionCreate>;

export const postB2bEmployeesRestrictionsEndpoint = endpoint({
  method: 'POST',
  body: ZodEmployeeRestrictionCreate,
  responseBody: z.object({ id: ZodId }),
});

export type PostB2bEmployeesRestrictionsEndpoint = GetEndpointType<
  typeof postB2bEmployeesRestrictionsEndpoint
>;

const ZodGetEmployeesRestrictionsInstruments = z.object({
  id: ZodId,
  full_name: ZodTrimmedString.nullable(),
});

export type GetEmployeesRestrictionsInstruments = z.TypeOf<
  typeof ZodGetEmployeesRestrictionsInstruments
>;

const ZodGetEmployeesRestrictionsBase = z.object({
  ...ZodEmployeeRestrictionBase.shape,
  instruments: z.array(ZodGetEmployeesRestrictionsInstruments),
});
export type GetEmployeesRestrictionsBase = z.TypeOf<typeof ZodGetEmployeesRestrictionsBase>;

export const DynamicRestrictionReasonValues = [
  'employee_status_past',
  'employee_status_retired',
  'terminated_contract',
  'open_withdrawal',
  'indirect_contract',
  'taiga',
  'triggered_arbitrage_not_allowed',
  'suspended_instrument_in_portfolio',
] as const;
export const ZodDynamicRestrictionReason = z.enum(DynamicRestrictionReasonValues);

export type DynamicRestrictionReason = z.TypeOf<typeof ZodDynamicRestrictionReason>;

export const ZodDynamicRestrictionFlagWithReason = z.object({
  flag: ZodEmployeeRestrictionsFlag,
  reason: ZodDynamicRestrictionReason,
});

export type DynamicRestrictionFlagWithReasonApi = z.TypeOf<
  typeof ZodDynamicRestrictionFlagWithReason
>;

const ZodDynamicRestrictionObject = z.object({
  restriction_flag: ZodDynamicRestrictionFlagWithReason.array(),
  plan_name: ZodTrimmedString,
});

export type DynamicRestrictionObjectBo = z.infer<typeof ZodDynamicRestrictionObject>;

const ZodDynamicRestrictions = z.object({
  restrictions: z.record(ZodProfileSavingPlanId, ZodDynamicRestrictionObject),
  has_global_restriction: z.boolean(),
});

export type DynamicRestrictionsBo = z.infer<typeof ZodDynamicRestrictions>;

const ZodGetB2bEmployeesRestrictionsResponse = z.object({
  restrictions: z.array(ZodGetEmployeesRestrictionsBase),
  company_restrictions: z.array(ZodCompanyRestrictionsFlag),
  id: ZodId,
  dynamic_restrictions: ZodDynamicRestrictions,
});
export type GetB2bEmployeesRestrictionsResponse = z.TypeOf<
  typeof ZodGetB2bEmployeesRestrictionsResponse
>;
export const getB2bEmployeesRestrictionsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bEmployeesRestrictionsResponse,
});

export type GetB2bEmployeesRestrictionsEndpoint = GetEndpointType<
  typeof getB2bEmployeesRestrictionsEndpoint
>;
